/**
 * AdminDashboard Page
 * Admin-only overview of all users and invitation codes.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AdminService } from '../services/AdminService';
import { ToastManager } from '../components/ToastContainer';
import { GlobalCriteriaEditor } from '../components/GlobalCriteriaEditor';
import type {
  AdminUserRecord,
  AdminUserDetail,
  AdminInvitationCodeDetail,
  AdminStats,
} from '../types';
import './AdminDashboard.css';

const TIERS = ['free', 'starter', 'professional', 'business', 'platinum', 'enterprise'];

function AdminDashboard() {
  const [adminService] = useState(() => new AdminService());

  // Stats state
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Users state
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [userDetail, setUserDetail] = useState<Record<string, AdminUserDetail>>({});
  const [detailLoading, setDetailLoading] = useState<Record<string, boolean>>({});

  // Edit modal state
  const [editTarget, setEditTarget] = useState<AdminUserRecord | null>(null);
  const [editTier, setEditTier] = useState('');
  const [editCredits, setEditCredits] = useState(0);
  const [editShowPrice, setEditShowPrice] = useState<boolean | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  // Invitation codes state
  const [codes, setCodes] = useState<AdminInvitationCodeDetail[]>([]);
  const [codesLoading, setCodesLoading] = useState(true);
  const [genTier, setGenTier] = useState('starter');
  const [genCount, setGenCount] = useState(5);
  const [genLoading, setGenLoading] = useState(false);
  const [generatedLinks, setGeneratedLinks] = useState<string[]>([]);

  // Invitation codes sort state
  type CodeSortField = 'used_at' | 'used_by_email';
  type SortDir = 'asc' | 'desc';
  const [codesSort, setCodesSort] = useState<{ field: CodeSortField | null; dir: SortDir }>({
    field: null,
    dir: 'desc',
  });

  // Note editing state
  const [noteEditing, setNoteEditing] = useState<string | null>(null);
  const [noteValue, setNoteValue] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  // Expiry editing state
  const [expiryEditing, setExpiryEditing] = useState<string | null>(null);
  const [expiryValue, setExpiryValue] = useState('');
  const [expirySaving, setExpirySaving] = useState(false);

  const toast = ToastManager.getInstance();

  // Load users and codes in parallel on mount
  useEffect(() => {
    let cancelled = false;

    adminService.getStats().then((data) => {
      if (!cancelled) setStats(data);
    }).catch((err) => {
      if (!cancelled) toast.error('Failed to load stats: ' + (err as Error).message);
    }).finally(() => {
      if (!cancelled) setStatsLoading(false);
    });

    adminService.getUsers().then((data) => {
      if (!cancelled) setUsers(data);
    }).catch((err) => {
      if (!cancelled) toast.error('Failed to load users: ' + (err as Error).message);
    }).finally(() => {
      if (!cancelled) setUsersLoading(false);
    });

    adminService.getInvitationCodes().then((data) => {
      if (!cancelled) setCodes(data);
    }).catch((err) => {
      if (!cancelled) toast.error('Failed to load invitation codes: ' + (err as Error).message);
    }).finally(() => {
      if (!cancelled) setCodesLoading(false);
    });

    return () => { cancelled = true; };
  }, []);

  // Expand / collapse user row; lazy-load detail
  const toggleExpand = useCallback(async (uid: string) => {
    if (expandedUserId === uid) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(uid);
    if (!userDetail[uid]) {
      setDetailLoading((prev) => ({ ...prev, [uid]: true }));
      try {
        const detail = await adminService.getUserDetail(uid);
        setUserDetail((prev) => ({ ...prev, [uid]: detail }));
      } catch (err) {
        toast.error('Failed to load user detail: ' + (err as Error).message);
      } finally {
        setDetailLoading((prev) => ({ ...prev, [uid]: false }));
      }
    }
  }, [expandedUserId, userDetail, adminService]);

  // Open edit modal
  const openEdit = (user: AdminUserRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditTarget(user);
    setEditTier(user.subscription_type);
    setEditCredits(user.credits_remaining);
    setEditShowPrice(user.show_price ?? null);
  };

  // Save edit
  const saveEdit = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await adminService.editUserSubscription(
        editTarget.user_id,
        editTier,
        editCredits,
        editShowPrice,
      );
      // Refresh user list
      const updated = await adminService.getUsers();
      setUsers(updated);
      // Invalidate cached detail for this user so it reloads on next expand
      setUserDetail((prev) => {
        const copy = { ...prev };
        delete copy[editTarget.user_id];
        return copy;
      });
      toast.success('Subscription updated successfully');
      setEditTarget(null);
    } catch (err) {
      toast.error('Failed to update subscription: ' + (err as Error).message);
    } finally {
      setEditSaving(false);
    }
  };

  // Generate invitation codes
  const handleGenerate = async () => {
    if (genCount < 1 || genCount > 100) {
      toast.error('Count must be between 1 and 100');
      return;
    }
    setGenLoading(true);
    setGeneratedLinks([]);
    try {
      const result = await adminService.generateCodes(genTier, genCount);
      setGeneratedLinks(result.registration_links);
      // Refresh codes list
      const updated = await adminService.getInvitationCodes();
      setCodes(updated);
      toast.success(`Generated ${result.count} code(s) for ${result.tier}`);
    } catch (err) {
      toast.error('Failed to generate codes: ' + (err as Error).message);
    } finally {
      setGenLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied to clipboard'),
      () => toast.error('Copy failed')
    );
  };

  // Codes table sort
  const toggleCodesSort = (field: CodeSortField) => {
    setCodesSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: 'desc' }
    );
  };

  const sortedCodes = useMemo(() => {
    if (!codesSort.field) return codes;
    const f = codesSort.field;
    return [...codes].sort((a, b) => {
      const valA = f === 'used_at' ? (a.used_at ?? '') : (a.used_by_email ?? '');
      const valB = f === 'used_at' ? (b.used_at ?? '') : (b.used_by_email ?? '');
      const cmp = valA.localeCompare(valB);
      return codesSort.dir === 'asc' ? cmp : -cmp;
    });
  }, [codes, codesSort]);

  // Note editing
  const startNoteEdit = (code: AdminInvitationCodeDetail, e: React.MouseEvent) => {
    e.stopPropagation();
    setNoteEditing(code.code);
    setNoteValue(code.note ?? '');
  };

  const saveNote = async (code: string) => {
    setNoteSaving(true);
    try {
      await adminService.updateCodeNote(code, noteValue);
      setCodes((prev) => prev.map((c) => c.code === code ? { ...c, note: noteValue || null } : c));
      setNoteEditing(null);
      toast.success('Note saved');
    } catch (err) {
      toast.error('Failed to save note: ' + (err as Error).message);
    } finally {
      setNoteSaving(false);
    }
  };

  // Expiry helpers
  const isoToDateInput = (iso: string | null): string => {
    if (!iso) return '';
    // datetime-local input expects "YYYY-MM-DDTHH:mm"
    return iso.slice(0, 16);
  };

  const startExpiryEdit = (code: AdminInvitationCodeDetail, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpiryEditing(code.code);
    setExpiryValue(isoToDateInput(code.expires_at));
  };

  const saveExpiry = async (code: string) => {
    setExpirySaving(true);
    try {
      // Convert local datetime-local value to UTC ISO string, or null to clear
      const isoValue = expiryValue ? new Date(expiryValue).toISOString() : null;
      await adminService.updateCodeExpiry(code, isoValue);
      setCodes((prev) => prev.map((c) => c.code === code ? { ...c, expires_at: isoValue } : c));
      setExpiryEditing(null);
      toast.success('Expiry updated');
    } catch (err) {
      toast.error('Failed to update expiry: ' + (err as Error).message);
    } finally {
      setExpirySaving(false);
    }
  };

  const handleDeleteCode = async (code: string) => {
    if (!window.confirm(`Delete invitation code ${code}? This cannot be undone.`)) return;
    try {
      await adminService.deleteCode(code);
      setCodes((prev) => prev.filter((c) => c.code !== code));
      toast.success('Invitation code deleted');
    } catch (err) {
      toast.error('Failed to delete code: ' + (err as Error).message);
    }
  };

  const getExpiryLabel = (expiresAt: string | null): { text: string; expired: boolean } => {
    if (!expiresAt) return { text: 'No expiry', expired: false };
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return { text: 'Expired', expired: true };
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return { text: `${days}d left`, expired: false };
    if (hours > 0) return { text: `${hours}h left`, expired: false };
    const mins = Math.floor((diff % 3600000) / 60000);
    return { text: `${mins}m left`, expired: false };
  };

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="admin-dashboard">
      <h1 className="admin-title">System Overview</h1>

      {/* ── Stats Overview ───────────────────────────────────────── */}
      <section className="admin-section">
        <h2 className="admin-section-title">SIFT Statistics</h2>
        {statsLoading ? (
          <div className="admin-stats-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="admin-skeleton-row" style={{ height: '80px' }} />
            ))}
          </div>
        ) : stats ? (
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <span className="admin-stat-label">Total Users</span>
              <span className="admin-stat-value">{stats.total_users.toLocaleString()}</span>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-label">Total Scans</span>
              <span className="admin-stat-value">{stats.total_scans.toLocaleString()}</span>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-label">Scans Today</span>
              <span className="admin-stat-value">{stats.scans_today.toLocaleString()}</span>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-label">This Week</span>
              <span className="admin-stat-value">{stats.scans_this_week.toLocaleString()}</span>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-label">This Month</span>
              <span className="admin-stat-value">{stats.scans_this_month.toLocaleString()}</span>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-label">Avg / Day</span>
              <span className="admin-stat-value">{stats.avg_scans_per_day}</span>
            </div>
          </div>
        ) : (
          <p className="admin-muted">Stats unavailable.</p>
        )}
      </section>

      {/* ── Users Section ─────────────────────────────────────────── */}
      <section className="admin-section">
        <h2 className="admin-section-title">Users</h2>

        {usersLoading ? (
          <div className="admin-skeleton-rows">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="admin-skeleton-row" />
            ))}
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Tier</th>
                  <th>Credits</th>
                  <th>Total Scans</th>
                  <th>Last Login</th>
                  <th>Admin</th>
                  <th>Show Price</th>
                  <th>Invite Code</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                   {users.length === 0 && (
                     <tr>
                       <td colSpan={10} className="admin-empty">No users found.</td>
                     </tr>
                   )}
                {users.map((user) => (
                  <React.Fragment key={user.user_id}>
                    <tr
                      className={`admin-user-row ${expandedUserId === user.user_id ? 'admin-user-row-expanded' : ''}`}
                      onClick={() => toggleExpand(user.user_id)}
                    >
                      <td className="admin-email">{user.email || <span className="admin-muted">{user.user_id.slice(0, 8)}…</span>}</td>
                      <td><span className={`admin-tier-badge admin-tier-${user.subscription_type}`}>{user.subscription_type}</span></td>
                      <td>{user.credits_remaining} / {user.credits_limit}</td>
                      <td className="admin-scan-count">
                        {stats
                          ? (stats.user_scan_counts[user.user_id] ?? 0).toLocaleString()
                          : <span className="admin-muted">—</span>
                        }
                      </td>
                      <td className="admin-date">{user.last_login_at ? formatDate(user.last_login_at) : <span className="admin-muted">Never</span>}</td>
                      <td>{user.is_admin ? <span className="admin-flag-yes">Yes</span> : <span className="admin-flag-no">No</span>}</td>
                      <td>{user.show_price ? <span className="admin-flag-yes">Yes</span> : <span className="admin-flag-no">No</span>}</td>
                      <td className="admin-monospace admin-invite-code">{user.invitation_code_used || <span className="admin-muted">—</span>}</td>
                      <td className="admin-date">{formatDate(user.created_at)}</td>
                      <td>
                        <button
                          className="admin-btn admin-btn-sm"
                          onClick={(e) => openEdit(user, e)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>

                    {expandedUserId === user.user_id && (
                       <tr className="admin-detail-row">
                         <td colSpan={10}>
                           {detailLoading[user.user_id] ? (
                            <div className="admin-detail-loading">Loading…</div>
                          ) : userDetail[user.user_id] ? (
                            <div className="admin-detail-panel">
                              <div className="admin-detail-grid">
                                <div className="admin-detail-item">
                                  <span className="admin-detail-label">User ID</span>
                                  <span className="admin-detail-value admin-monospace">{user.user_id}</span>
                                </div>
                                <div className="admin-detail-item">
                                  <span className="admin-detail-label">Subscription Status</span>
                                  <span className="admin-detail-value">
                                    <span className={`admin-status-badge admin-status-${userDetail[user.user_id].status}`}>{userDetail[user.user_id].status}</span>
                                  </span>
                                </div>
                                <div className="admin-detail-item">
                                  <span className="admin-detail-label">Billing Period</span>
                                  <span className="admin-detail-value">
                                    {formatDate(userDetail[user.user_id].billing_period_start)} → {formatDate(userDetail[user.user_id].billing_period_end)}
                                  </span>
                                </div>
                                <div className="admin-detail-item">
                                  <span className="admin-detail-label">Stripe Customer ID</span>
                                  <span className="admin-detail-value admin-monospace">{userDetail[user.user_id].stripe_customer_id || '—'}</span>
                                </div>
                                 <div className="admin-detail-item">
                                   <span className="admin-detail-label">Tutorial Completed</span>
                                   <span className="admin-detail-value">{userDetail[user.user_id].tutorial_completed ? 'Yes' : 'No'}</span>
                                 </div>
                                 <div className="admin-detail-item">
                                   <span className="admin-detail-label">Show Price</span>
                                   <span className="admin-detail-value">{userDetail[user.user_id].show_price ? 'Yes' : 'No'}</span>
                                 </div>
                                 <div className="admin-detail-item">
                                   <span className="admin-detail-label">Criteria Count</span>
                                   <span className="admin-detail-value">{userDetail[user.user_id].criteria_count}</span>
                                 </div>
                              </div>
                            </div>
                          ) : (
                            <div className="admin-detail-loading">Failed to load detail.</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Invitation Codes Section ───────────────────────────────── */}
      <section className="admin-section">
        <h2 className="admin-section-title">Invitation Codes</h2>

        {/* Generate form */}
        <div className="admin-generate-form">
          <label className="admin-field-label">
            Tier
            <select
              className="admin-select"
              value={genTier}
              onChange={(e) => setGenTier(e.target.value)}
            >
              {TIERS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
          <label className="admin-field-label">
            Count (1–100)
            <input
              type="number"
              className="admin-input"
              min={1}
              max={100}
              value={genCount}
              onChange={(e) => setGenCount(parseInt(e.target.value, 10) || 1)}
            />
          </label>
          <button
            className="admin-btn admin-btn-primary"
            onClick={handleGenerate}
            disabled={genLoading}
          >
            {genLoading ? 'Generating…' : 'Generate Codes'}
          </button>
        </div>

        {/* Generated links */}
        {generatedLinks.length > 0 && (
          <div className="admin-generated-links">
            <p className="admin-generated-title">Registration links (click to copy):</p>
            {generatedLinks.map((link, i) => (
              <div key={i} className="admin-generated-link-row">
                <span className="admin-generated-link-text">{link}</span>
                <button
                  className="admin-btn admin-btn-sm"
                  onClick={() => copyToClipboard(link)}
                >
                  Copy
                </button>
              </div>
            ))}
          </div>
        )}

        {codesLoading ? (
          <div className="admin-skeleton-rows">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="admin-skeleton-row" />
            ))}
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th
                    className="admin-th-sortable"
                    onClick={() => toggleCodesSort('used_at')}
                  >
                    Used At {codesSort.field === 'used_at' ? (codesSort.dir === 'asc' ? '↑' : '↓') : <span className="admin-sort-hint">↕</span>}
                  </th>
                  <th
                    className="admin-th-sortable"
                    onClick={() => toggleCodesSort('used_by_email')}
                  >
                    Used By {codesSort.field === 'used_by_email' ? (codesSort.dir === 'asc' ? '↑' : '↓') : <span className="admin-sort-hint">↕</span>}
                  </th>
                  <th>Generated By</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {sortedCodes.length === 0 && (
                  <tr>
                    <td colSpan={9} className="admin-empty">No invitation codes found.</td>
                  </tr>
                )}
                {sortedCodes.map((code) => (
                  <tr key={code.code} className={code.used ? 'admin-code-used' : 'admin-code-unused'}>
                    <td>
                      <div className="admin-code-cell">
                        <span className="admin-monospace">{code.code}</span>
                        <button
                          className="admin-btn admin-btn-sm admin-btn-icon"
                          title="Copy registration link"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(`${window.location.origin}/register?code=${encodeURIComponent(code.code)}`);
                          }}
                        >
                          ⎘
                        </button>
                      </div>
                    </td>
                    <td><span className={`admin-tier-badge admin-tier-${code.tier}`}>{code.tier}</span></td>
                    <td>
                      {code.used
                        ? <span className="admin-status-badge admin-status-used">Used</span>
                        : <span className="admin-status-badge admin-status-unused">Unused</span>
                      }
                    </td>
                    <td className="admin-date">{formatDate(code.created_at)}</td>
                    <td>
                      {expiryEditing === code.code ? (
                        <div className="admin-note-edit">
                          <input
                            type="datetime-local"
                            className="admin-input admin-expiry-input"
                            value={expiryValue}
                            onChange={(e) => setExpiryValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') setExpiryEditing(null);
                              if (e.key === 'Enter') saveExpiry(code.code);
                            }}
                            autoFocus
                          />
                          <button
                            className="admin-btn admin-btn-sm admin-btn-primary"
                            onClick={() => saveExpiry(code.code)}
                            disabled={expirySaving}
                          >
                            {expirySaving ? '…' : 'Save'}
                          </button>
                          <button
                            className="admin-btn admin-btn-sm"
                            onClick={() => setExpiryEditing(null)}
                            disabled={expirySaving}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (() => {
                        const { text, expired } = getExpiryLabel(code.expires_at);
                        return (
                          <div className="admin-expiry-display">
                            <span className={expired ? 'admin-expiry-expired' : code.expires_at ? 'admin-expiry-active' : 'admin-muted'}>
                              {text}
                            </span>
                            {!code.used && (
                              <button
                                className="admin-btn admin-btn-sm"
                                onClick={(e) => startExpiryEdit(code, e)}
                                title="Edit expiry"
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="admin-date">{formatDate(code.used_at)}</td>
                    <td className="admin-email">{code.used_by_email || <span className="admin-muted">—</span>}</td>
                    <td className="admin-email">{code.generated_by_email || <span className="admin-muted">—</span>}</td>
                    <td>
                      {noteEditing === code.code ? (
                        <div className="admin-note-edit">
                          <input
                            className="admin-input admin-note-input"
                            value={noteValue}
                            onChange={(e) => setNoteValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') setNoteEditing(null);
                              if (e.key === 'Enter') saveNote(code.code);
                            }}
                            autoFocus
                          />
                          <button
                            className="admin-btn admin-btn-sm admin-btn-primary"
                            onClick={() => saveNote(code.code)}
                            disabled={noteSaving}
                          >
                            {noteSaving ? '…' : 'Save'}
                          </button>
                          <button
                            className="admin-btn admin-btn-sm"
                            onClick={() => setNoteEditing(null)}
                            disabled={noteSaving}
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="admin-note-display">
                          <span className={code.note ? 'admin-note-text' : 'admin-muted'}>
                            {code.note || '—'}
                          </span>
                          <button
                            className="admin-btn admin-btn-sm"
                            onClick={(e) => startNoteEdit(code, e)}
                          >
                            Edit
                          </button>
                          <button
                            className="admin-btn admin-btn-sm admin-btn-danger"
                            onClick={(e) => { e.stopPropagation(); handleDeleteCode(code.code); }}
                            title="Delete invitation code"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Global Criteria Section ──────────────────────────────── */}
      <section className="admin-section">
        <GlobalCriteriaEditor adminService={adminService} />
      </section>

      {/* ── Edit Modal ────────────────────────────────────────────── */}
      {editTarget && (
        <div className="admin-modal-backdrop" onClick={() => setEditTarget(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal-title">Edit Subscription</h3>
            <p className="admin-modal-subtitle">{editTarget.email || editTarget.user_id}</p>

            <label className="admin-field-label">
              Subscription Tier
              <select
                className="admin-select"
                value={editTier}
                onChange={(e) => setEditTier(e.target.value)}
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>

            <label className="admin-field-label">
              Credits Remaining
              <input
                type="number"
                className="admin-input"
                min={0}
                value={editCredits}
                onChange={(e) => setEditCredits(parseInt(e.target.value, 10) || 0)}
              />
            </label>

            <label className="admin-field-label">
              Show Price Information
              <div style={{ marginTop: '0.25rem' }}>
                <label style={{ marginRight: '1rem' }}>
                  <input
                    type="radio"
                    name="showPrice"
                    checked={editShowPrice === true}
                    onChange={() => setEditShowPrice(true)}
                  /> Yes
                </label>
                <label style={{ marginRight: '1rem' }}>
                  <input
                    type="radio"
                    name="showPrice"
                    checked={editShowPrice === false}
                    onChange={() => setEditShowPrice(false)}
                  /> No
                </label>
                <label>
                  <input
                    type="radio"
                    name="showPrice"
                    checked={editShowPrice === null}
                    onChange={() => setEditShowPrice(null)}
                  /> Don't change
                </label>
              </div>
            </label>

            <div className="admin-modal-actions">
              <button
                className="admin-btn"
                onClick={() => setEditTarget(null)}
                disabled={editSaving}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={saveEdit}
                disabled={editSaving}
              >
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
